<template>
    <div class="changePlaNew">
        <form-message v-if="form.errors.has('stripe')" :message="form.errors.get('stripe')" />
        <form-message
            v-else-if="form.errors.get('card_error.0')"
            :message="form.errors.get('card_error.0')"
        />
        <form-message v-else-if="form.errors.get('error')" :message="form.errors.get('error')" />
        <form-message
            v-else-if="!form.errors.get('name') && form.errors.any()"
            message="Something went wrong, please try again."
        />
        <form-message
            v-if="hasCoupon"
            :message="couponMessage"
            :type="'is-success'"
            isHtml
            :icon="false"
        />

        <p v-if="!isOrganizationOwner">
            Please contact your administrator about Upgrading to another plan.
        </p>

        <div v-if="hasNotCard" class="columns">
            <div class="column">
                <div v-if="prorateLoading">
                    <div class="card-content has-text-centered" style="justify-content: center">
                        <basic-loader :center="true" aria-label="ChangePlanNew" />
                    </div>
                </div>
                <template v-else>
                    <form-message v-if="stripeError" :message="stripeError" />

                    <template v-if="isProrate">
                        <p class="field changePlaNew__title">
                            <span>Order summary</span>
                        </p>

                        <upgrade-table-total :table="table" />

                        <hr />
                    </template>
                    <template v-else>
                        <p class="field changePlaNew__title">
                            <span>Order summary</span>
                        </p>
                        <p class="field">
                            {{ planNameFormatted }}
                        </p>
                        <span
                            v-if="defaultType.includes('annual')"
                            class="upgradePlanItem__note"
                            style="text-align: left"
                        >
                            <sup>*</sup>Billed annually
                        </span>

                        <hr />
                    </template>

                    <p class="field">
                        <b>Add Payment Information</b>
                    </p>

                    <form-input
                        v-model="form.name"
                        label="Card Holder Name"
                        placeholder="Card Holder Name"
                        :error="form.errors.get('name')"
                        @input="form.errors.clear('name')"
                    />

                    <div
                        class="field"
                        :style="{
                            opacity: !prorateLoading ? 1 : 0,
                            height: !prorateLoading ? 'auto' : 0,
                        }"
                    >
                        <label class="label"> Card Details </label>
                        <div class="control">
                            <div id="create-card" class="input" />
                        </div>
                    </div>
                </template>
            </div>
        </div>
        <div v-else class="columns">
            <div class="column">
                <div v-if="prorateLoading">
                    <div class="card-content has-text-centered" style="justify-content: center">
                        <basic-loader :center="true" aria-label="ChangePlanNew" />
                    </div>
                </div>
                <div v-else class="upgrade-plan__modal">
                    <div class="column is-12 changePlaNew__title">
                        <span>Order summary</span>
                    </div>

                    <upgrade-table-total :table="table" />
                </div>
            </div>
        </div>

        <credits-auto-recharge v-if="!autoRechargeStatus && !prorateLoading && isSubscription" />
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import Form from 'core/Form';
import CreditsAutoRecharge from 'components/Broadcasts/CreditsAutoRecharge';
import CardsMixin from 'mixins/cards';
import OrganizationsMixin from 'mixins/organization';
import AutoRechargeMixin from 'mixins/auto-recharge';
import ProrateMixin from 'mixins/prorate';
import UpgradeTableTotal from './UpgradeTableTotal.vue';
import { segmentEvent } from 'services/segment';
import { CancelToken } from 'core/api';
import filters from '../../../../vue/filters';

export default {
    emits: ['handleChangeData', 'closed', 'showError'],

    components: {
        CreditsAutoRecharge,
        UpgradeTableTotal,
    },

    mixins: [CardsMixin, OrganizationsMixin, AutoRechargeMixin, ProrateMixin],

    props: {
        defaultPlan: { type: String, default: '' },
        defaultType: { type: String, default: '' },
        target: { type: String, default: '' },
        hasCard: { type: Boolean },
        price: { type: [String, Number], default: '' },
        resetPlan: { type: Boolean, default: false },
        currentPlan: { type: Object, required: true },
        defaultPlanName: { type: String, default: '' },
        tablePrices: { type: Array, default: () => [] },
    },

    data() {
        return {
            prorateLoading: true,
            prorate: null,
            form: new Form(
                {
                    interval: 'yearly',
                    token: '',
                    primary: true,
                    backup: false,
                    amount: 0,
                    auto_recharge: false,
                },
                false
            ),
            stripeForm: new Form(
                {
                    name: '',
                    country: 'US',
                    city: '',
                    region: '',
                    zip: '',
                },
                false
            ),
            axiosCanceler: null,
            showCardDuringRequest: false,
        };
    },

    computed: {
        taxTitle() {
            if (Array.isArray(this.prorate?.tax_rates) && this.prorate?.tax_rates?.length !== 0) {
                let taxStates = '';
                this.prorate.tax_rates.forEach((state) => {
                    if (state.jurisdiction)
                        taxStates = taxStates
                            ? `${taxStates} / ${state.jurisdiction}`
                            : state.jurisdiction;
                });

                if (taxStates) return `Salex Tax - ${taxStates}`;

                return 'Salex Tax';
            } else {
                return 'Salex Tax';
            }
        },

        table() {
            const columns = [
                {
                    typeColumn: 'small',
                    typeDefault: this.typeName,
                    isCard: !this.hasNotCard,
                    label: this.planNameFormatted,
                    total: filters.currency(this.linesPlanAmount, 2),
                },
            ];

            const total = this.tablePrices.find((item) => item.type === 'total');
            const items = this.tablePrices.filter((item) => item.type === 'item');

            if (this.tablePrices.length) {
                columns.push({
                    typeColumn: 'small',
                    isLast: false,
                    typeDefault: this.typeName,
                    type: 'collapse-button',
                    isCard: !this.hasNotCard,
                    label: 'Fees',
                    total: filters.currency(total.price, 2),
                });
            }

            if (this.prorate.quantity !== 0) {
                columns.push({
                    typeColumn: 'small',
                    typeDefault: this.typeName,
                    isCard: !this.hasNotCard,
                    label: `- Plus ${filters.number(this.prorate.quantity, 0)} seat${this.prorate.quantity > 1 ? 's' : ''}`,
                    total: filters.currency(this.linesSeatAmount, 2),
                });
            }

            if (this.prorate.shared_inboxes_quantity > 0) {
                columns.push({
                    typeColumn: 'small',
                    typeDefault: this.typeName,
                    isCard: !this.hasNotCard,
                    label: `- Plus ${filters.number(this.prorate.shared_inboxes_quantity, 0)} number${this.prorate.shared_inboxes_quantity > 1 ? 's' : ''}`,
                    total: filters.currency(this.linesInboxAmount, 2),
                });
            }

            if (this.prorate.lines.find((line) => line.key === 'compliance')) {
                columns.push({
                    typeColumn: 'small',
                    typeDefault: this.typeName,
                    isCard: !this.hasNotCard,
                    label: `- Plus Calling Compliance @ ${this.form.interval === 'yearly' ? '$120/year' : '$10/month'}`,
                    total: filters.currency(this.linesComplianceAmount, 2),
                });
            }

            if (this.prorate.lines.find((line) => line.key === 'custom_domain')) {
                columns.push({
                    typeColumn: 'small',
                    typeDefault: this.typeName,
                    isCard: !this.hasNotCard,
                    label: '- Plus custom domain',
                    total: filters.currency(this.linesCustomDomainAmount, 2),
                });
            }

            if (this.prorate.credit_amount) {
                columns.push({
                    typeColumn: 'small',
                    isLast: !this.tablePrices.length,
                    typeDefault: this.typeName,
                    isCard: !this.hasNotCard,
                    label: `Unused time on ${this.subscriptionPlanName} after ${this.today}:`,
                    total: filters.currency(this.prorate.credit_amount, 2),
                });
            }

            items.forEach((item, index) => {
                columns.push({
                    typeColumn: 'small',
                    isLast: items.length === index + 1,
                    typeDefault: this.typeName,
                    type: 'collapsed',
                    tooltip: item.tooltip,
                    isCard: !this.hasNotCard,
                    label: item.label,
                    total: item.info ? item.price : filters.currency(item.priceNumber, 2),
                });
            });

            if (this.hasCoupon) {
                columns.push({
                    typeColumn: 'small',
                    typeDefault: this.typeName,
                    isCard: !this.hasNotCard,
                    label: `Discount (-${this.percentCoupon})`,
                    total: this.percentDiscountTotal,
                });
            }

            if (this.prorate?.taxes_amount) {
                columns.push({
                    typeColumn: 'small',
                    typeDefault: this.typeName,
                    isCard: !this.hasNotCard,
                    label: this.taxTitle,
                    total: filters.currency(this.prorate.taxes_amount, 2),
                });
            }

            columns.push({
                typeColumn: 'total',
                typeDefault: this.typeName,
                isCard: !this.hasNotCard,
                label: 'Amount charged today',
                total: filters.currency(
                    total
                        ? total.priceNumber + this.prorate.prorated_amount
                        : this.prorate.prorated_amount,
                    2
                ),
            });

            columns.push({
                typeColumn: 'footer',
                typeDefault: this.typeName,
                isCard: !this.hasNotCard,
                label: `The next charge on your ${this.form.interval} billing cycle will be on ${filters.date(this.prorate.next_charge_date)}`,
            });

            return columns;
        },

        hasNotCard() {
            return Boolean(
                (!this.hasCard &&
                    (this.hasNotBillingSources ||
                        this.showCardDuringRequest ||
                        this.isCancelled ||
                        this.isSuspended ||
                        this.isUnpaid ||
                        !this.isSubscription)) ||
                    this.hasUnpaidStatusWithCard
            );
        },

        hasUnpaidStatusWithCard() {
            return Boolean(this.hasCard && this.isUnpaid);
        },

        currentDateFormatted() {
            return moment.utc(this.subscription.period_ends_at).format('MMMM D, Y');
        },

        planName() {
            return 'Pro';
        },

        typeName() {
            if (this.defaultType.includes('monthly')) {
                return 'Monthly';
            }

            return 'Annual';
        },

        planNameFormatted() {
            if (this.hasNotCard) {
                if (this.defaultType.includes('monthly')) {
                    return `Pro - $${this.price} / Monthly`;
                }

                return `Pro - $${this.price} / Monthly*`;
            }

            return `${this.planName} - ${filters.currency(this.linesPlanAmount, 2)} / ${this.typeName}`;
        },

        ...mapState(stores.Subscriptions, ['subscription']),
        ...mapState(stores.Organization, ['featureFlags']),
    },

    watch: {
        prorateLoading(status) {
            if (!status) {
                if (!this.hasCard || this.hasUnpaidStatusWithCard) {
                    this.$nextTick(() => {
                        this.initStripe();
                        this.initStripeCard('#create-card');
                    });
                }
            }
        },
    },

    methods: {
        async calculateProrate() {
            this.axiosCanceler = CancelToken.source();

            if (!this.defaultPlan) {
                return;
            }

            if (!this.isSubscription) {
                this.prorateLoading = false;

                return;
            }

            try {
                this.prorateLoading = true;
                this.$emit('handleChangeData', ['form', 'busy', true]);
                this.axiosCanceler.cancel();
                this.axiosCanceler = CancelToken.source();

                const params = {
                    plan: this.defaultPlan,
                };

                this.prorate = await this.$store.dispatch('getBillingPreviewPlanMicroservice', {
                    cancelToken: this.axiosCanceler.token,
                    params: params,
                });

                this.prorateLoading = false;
                this.$emit('handleChangeData', ['form', 'busy', false]);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'calculateProrate');
                this.prorateLoading = false;
                this.$emit('handleChangeData', ['form', 'busy', false]);
            }
        },

        reset() {
            this.form.reset();
            this.stripeForm.reset();
            this.stripeError = null;
        },

        closed() {
            this.reset();
            this.$emit('closed', false);
        },

        async handleResetPlan() {
            try {
                this.$emit('handleChangeData', ['form', 'busy', true]);
                const response = await this.$store.dispatch('updateBillingUpgradeResetCycle');

                let credits = this.prorate.incoming_credits;

                await this.$store.dispatch('accountBalanceUpdated', {
                    account_balance: this.organization.account_balance,
                    account_credits: credits,
                    trial_credits: this.organization.trial_credits,
                });
                this.$store.dispatch('setUserData', response.data);

                this.$emit('handleChangeData', ['form', 'successful', true]);

                setTimeout(() => {
                    this.$emit('handleChangeData', ['form', 'successful', false]);
                    this.closed();
                }, 1500);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'save');

                if (e.response.data.card_error) {
                    this.form.errors.record({ card_error: [e.response.data.card_error] });
                }
            } finally {
                this.$emit('handleChangeData', ['form', 'busy', false]);
            }
        },

        async handleExistCard() {
            await this.doUpgrade();
            this.$emit('handleChangeData', ['form', 'busy', false]);
        },

        async handleNotExistCard() {
            try {
                this.form.busy = true;
                this.stripeError = null;

                if (!this.form.name) {
                    this.form.errors.record({ name: ['Card Holder Name is required'] });
                    this.form.busy = false;
                } else {
                    this.form.errors.clear();
                }

                const result = await this.stripe.createToken(this.stripeCard, {
                    name: `${this.stripeForm.name.trim()}`,
                    address_city: this.stripeForm.city,
                    address_state: this.stripeForm.region,
                    address_country: this.stripeForm.country,
                });

                if (result.error) throw result;
                if (!this.form.name) {
                    this.form.busy = false;
                    this.$emit('handleChangeData', ['form', 'busy', false]);

                    return;
                }

                this.form.token = result.token.id;

                await this.doUpgrade();
            } catch (e) {
                this.$emit('handleChangeData', ['form', 'busy', false]);

                if (e.error) {
                    this.stripeError = e.error.message;
                } else {
                    this.stripeError =
                        e.message || 'Failed to save your payment method, please try again.';
                }
            }
        },

        async handleSubscriptionCanceled() {
            try {
                this.form.busy = true;
                this.stripeError = null;

                if (!this.form.name) {
                    this.form.errors.record({ name: ['Card Holder Name is required'] });
                    this.form.busy = false;
                } else {
                    this.form.errors.clear();
                }

                const result = await this.stripe.createToken(this.stripeCard, {
                    name: `${this.stripeForm.name.trim()}`,
                    address_city: this.stripeForm.city,
                    address_state: this.stripeForm.region,
                    address_country: this.stripeForm.country,
                });

                if (result.error) throw result;
                if (!this.form.name) {
                    this.form.busy = true;

                    return;
                }

                this.form.token = result.token.id;

                const params = {
                    token: this.form.token,
                    plan: this.defaultPlan,
                };

                try {
                    await this.$store.dispatch('updateBillingRestartMicroservice', { params });
                } catch (e) {
                    const error_message =
                        e.response.data?.card_error || Object.values(e.response.data)[0];
                    throw new Error(error_message);
                }

                this.$emit('handleChangeData', ['form', 'busy', false]);
                this.$emit('handleChangeData', ['form', 'successful', true]);

                setTimeout(() => {
                    this.$emit('handleChangeData', ['form', 'successful', false]);
                    this.closed();
                    window.location = '/';
                }, 1500);
            } catch (e) {
                this.$emit('handleChangeData', ['form', 'busy', false]);

                if (e.error) {
                    this.stripeError = e.error.message;
                } else {
                    this.stripeError =
                        e.message || 'Failed to save your payment method, please try again.';
                }
            }
        },

        async save() {
            this.$emit('handleChangeData', ['form', 'busy', true]);

            if (this.resetPlan && !this.isCancelled) {
                await this.handleResetPlan();

                return;
            }

            if (this.isCancelled) {
                await this.handleSubscriptionCanceled();

                return;
            }

            if (!this.hasCard || this.hasUnpaidStatusWithCard) {
                this.showCardDuringRequest = true;
                await this.handleNotExistCard();

                return;
            }

            if (this.hasCard) {
                await this.handleExistCard();
            }
        },

        async doUpgrade() {
            try {
                let isSubscription = this.isSubscription;
                let isIncompleteStatus = ['canceled', 'unpaid', 'incomplete_expired'].includes(
                    this.subscriptionStatus
                );
                let isOnTrial = this.isOnTrial;

                const params = {
                    plan: this.defaultPlan,
                };

                if (this.form.token) {
                    params.token = this.form.token;
                }

                await this.$store.dispatch('updateBillingUpgradePlanMicroservice', { params });
                await this.$store.dispatch('getUserMe');

                await this.$store.dispatch('accountBalanceUpdated', {
                    account_balance: this.organization.account_balance,
                    account_credits: this.organization.account_credits,
                    trial_credits: this.organization.trial_credits,
                });

                window.publicApiReact?.openUpgradePlanModalFinishStore(params.plan);

                if (!isSubscription) {
                    this.showCardDuringRequest = false;
                    this.$emit('handleChangeData', ['form', 'successful', true]);

                    setTimeout(() => {
                        this.closed();
                        this.$router.replace('/register');
                    }, 1500);

                    return;
                }

                this.showCardDuringRequest = false;
                this.$emit('handleChangeData', ['form', 'successful', true]);

                if (isOnTrial) {
                    segmentEvent(
                        'Purchase',
                        {
                            value: this.target === 'monthly' ? this.price : this.price * 12,
                        },
                        {
                            context: {
                                organizationId: this.organization.id,
                            },
                        }
                    );
                }

                setTimeout(() => {
                    this.closed();

                    if (isOnTrial) {
                        this.$router.push('/settings/organization/compliance');
                    }

                    if (isIncompleteStatus) {
                        window.location = '/';
                    }
                }, 1500);
            } catch (error) {
                this.form.errors.record(error.response.data);

                if (error && error.response.data.hasOwnProperty('stripe')) {
                    let subscription = cloneDeep(this.subscription);

                    subscription['status'] = 'unpaid';
                    subscription['suspended_for'] = 'failed-plan-upgrade';
                }

                setTimeout(() => {
                    this.$emit('handleChangeData', ['form', 'successful', false]);
                    this.$emit('handleChangeData', ['showError', true]);
                }, 1500);
            } finally {
                this.$emit('handleChangeData', ['form', 'busy', false]);
            }
        },
    },

    async mounted() {
        if (this.defaultType.includes('monthly')) {
            this.form.interval = 'monthly';
        } else {
            this.form.interval = 'yearly';
        }

        this.calculateProrate();
    },
};
</script>

<style lang="scss" scoped>
@import './styles.module.scss';
</style>
